// [profiles+follows] Public user profile page (`/u/:username`) — horizontal header + Overview/Skills
// tabs + single Edit-profile modal + follow. Remove with the feature (see docs/user-profiles-and-follows.md).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import Pagination, { usePagination } from "@/components/Pagination";

import {
  checkUsername,
  followUser,
  getFollowers,
  getFollowing,
  getProfile,
  getUserSkills,
  unfollowUser,
  updateProfile,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useSeoMeta } from "@/lib/useSeoMeta";
import type { FollowUser, Profile, Skill, SocialLinks } from "@/lib/types";

type SocialKey = keyof SocialLinks;
const SOCIALS: { key: SocialKey; label: string; placeholder: string }[] = [
  { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/you" },
  { key: "github", label: "GitHub", placeholder: "https://github.com/you" },
  { key: "x", label: "X", placeholder: "https://x.com/you" },
];

function initials(name: string) {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";
}

// Compact relative time for a skill card, e.g. "5m ago", "23h ago", "1 day ago", "3mo ago".
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

// Strip protocol/www/trailing slash so a stored URL reads as a recognizable handle (github.com/you).
function prettyUrl(u: string) {
  try {
    const url = new URL(u);
    return (url.hostname.replace(/^www\./, "") + url.pathname).replace(/\/$/, "");
  } catch {
    return u;
  }
}

function Avatar({ url, name, size }: { url: string | null; name: string; size: number }) {
  if (url) {
    return <img src={url} alt={name} style={{ width: size, height: size }} className="shrink-0 rounded-full object-cover" />;
  }
  return (
    <span
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: "var(--grad-emphasis)" }}
      className="flex shrink-0 items-center justify-center rounded-full font-display font-bold text-white"
    >
      {initials(name)}
    </span>
  );
}

function BoltIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function SocialIcon({ kind }: { kind: SocialKey }) {
  const common = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "currentColor" } as const;
  if (kind === "github") {
    return (
      <svg {...common}><path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.3-1.3-1.7-1.3-1.7-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.8-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.4-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2a11.4 11.4 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.3c0 .4.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5Z" /></svg>
    );
  }
  if (kind === "linkedin") {
    return (
      <svg {...common}><path d="M20.45 20.45h-3.56v-5.57c0-1.33 0-3.04-1.85-3.04s-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.77C.8 0 0 .77 0 1.73v20.54C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" /></svg>
    );
  }
  return (
    <svg {...common}><path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.21-6.81-5.96 6.81H1.68l7.73-8.84L1.25 2.25h6.82l4.71 6.23 5.46-6.23Zm-1.16 17.52h1.83L7.01 4.13H5.05l12.03 15.64Z" /></svg>
  );
}

// Design-system eyebrow: an orange dash + mono uppercase label (matches the Explore page).
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

export default function ProfilePage() {
  const username = String(useParams().username);
  const navigate = useNavigate();
  const { user, accessToken, refreshUser } = useAuth();

  const [profile, setProfile] = useState<Profile | null>(null);
  useSeoMeta({
    title: profile ? `${profile.display_name} (@${profile.username}) — SkillPatch` : "SkillPatch",
    description: profile?.bio || undefined,
    path: `/u/${username}`,
  });
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [followBusy, setFollowBusy] = useState(false);
  const [listOpen, setListOpen] = useState<"followers" | "following" | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  // Active tab lives in the URL (?tab=skills) so browser Back restores it — clicking a skill from the
  // Skills tab and returning lands you back on Skills, not Overview. Overview is the default (no param).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: "overview" | "skills" = searchParams.get("tab") === "skills" ? "skills" : "overview";
  const setTab = (t: "overview" | "skills") =>
    setSearchParams(t === "skills" ? { tab: "skills" } : {}, { replace: true });

  const load = useCallback(async () => {
    setError(null);
    try {
      setProfile(await getProfile(username, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Profile not found");
      setProfile(null);
    }
  }, [username, accessToken]);

  useEffect(() => { load(); }, [load]);

  // The user's published skills — fetched once the profile resolves; failures leave an empty list.
  useEffect(() => {
    let cancelled = false;
    setSkills(null);
    getUserSkills(username, accessToken)
      .then((s) => { if (!cancelled) setSkills(s); })
      .catch(() => { if (!cancelled) setSkills([]); });
    return () => { cancelled = true; };
  }, [username, accessToken]);

  // Stable list reference so pagination doesn't reset every render; 20 per page (hook default).
  const skillsList = useMemo(() => skills ?? [], [skills]);
  const pager = usePagination(skillsList);

  async function toggleFollow() {
    if (!profile || !accessToken || followBusy) return;
    setFollowBusy(true);
    try {
      const res = profile.is_following
        ? await unfollowUser(profile.username, accessToken)
        : await followUser(profile.username, accessToken);
      setProfile((p) => (p ? { ...p, is_following: res.following, followers: res.followers } : p));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't update follow");
    } finally {
      setFollowBusy(false);
    }
  }

  async function onSaved(updated: Profile) {
    const renamed = updated.username !== profile?.username;
    setProfile(updated);
    setEditOpen(false);
    if (renamed) { await refreshUser(); navigate(`/u/${updated.username}`, { replace: true }); }
  }

  // Email visibility toggles inline (Hide/Show) — kept out of the edit modal.
  async function toggleEmail() {
    if (!accessToken || !profile) return;
    try {
      setProfile(await updateProfile({ show_email: !profile.show_email }, accessToken));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't update email visibility");
    }
  }

  // Save a single field from the inline Add/Edit controls in the About column. Throws so the
  // inline editor can surface its own error.
  async function patch(body: Parameters<typeof updateProfile>[0]) {
    setProfile(await updateProfile(body, accessToken!));
  }

  if (error) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-24 text-center">
        <p className="text-lg text-text">This profile isn't here</p>
        <p className="mt-1 text-sm text-text/50">{error}</p>
        <Link to="/skills" className="mt-5 inline-block text-sm font-medium text-primary hover:underline">← Explore skills</Link>
      </main>
    );
  }
  if (!profile) {
    return <main className="mx-auto max-w-2xl px-6 py-24 text-center text-sm text-text/40">Loading profile…</main>;
  }

  const owner = profile.is_self;
  const canFollow = !!user && !owner;
  const recent = skillsList.slice(0, 3); // Overview shows the 3 most recent; Skills tab paginates the rest.

  return (
    <main className="px-6 py-10 sm:px-12 lg:px-20 xl:px-28">
      {/* Header — avatar, name/@username, inline stats, action button */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <Avatar url={profile.avatar_url} name={profile.display_name} size={96} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold tracking-tight text-text">{profile.display_name}</h1>
              <p className="mt-0.5 font-mono text-sm text-text/50">@{profile.username}</p>
            </div>
            {owner ? (
              <button onClick={() => setEditOpen(true)}
                className="shrink-0 rounded-xl border border-[var(--border)] bg-[var(--glass)] px-5 py-2 text-sm font-semibold text-text/80 transition hover:border-primary/40 hover:text-text">
                Edit profile
              </button>
            ) : canFollow ? (
              <button onClick={toggleFollow} disabled={followBusy}
                className={`shrink-0 rounded-xl px-6 py-2 text-sm font-semibold transition disabled:opacity-50 ${
                  profile.is_following
                    ? "border border-[var(--border)] text-text/80 hover:border-red-400 hover:text-red-500"
                    : "bg-primary text-white shadow-btn hover:bg-primary-hover"
                }`}>
                {profile.is_following ? "Following" : "Follow"}
              </button>
            ) : !user ? (
              <Link to={`/login?next=${encodeURIComponent(`/u/${profile.username}`)}`}
                className="shrink-0 rounded-xl bg-primary px-6 py-2 text-sm font-semibold text-white shadow-btn hover:bg-primary-hover">
                Follow
              </Link>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-sm text-text/55">
            <button onClick={() => setListOpen("followers")} className="transition hover:text-primary">
              <span className="font-semibold text-text/85">{profile.followers}</span> followers
            </button>
            <button onClick={() => setListOpen("following")} className="transition hover:text-primary">
              <span className="font-semibold text-text/85">{profile.following}</span> following
            </button>
            <button onClick={() => setTab("skills")} className="transition hover:text-primary">
              <span className="font-semibold text-text/85">{profile.public_skills}</span> published skills
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="mt-8 flex gap-7 border-b border-[var(--border)]">
        {(["overview", "skills"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`relative pb-3 font-display text-sm font-medium capitalize transition ${tab === t ? "text-text" : "text-text/50 hover:text-text/80"}`}>
            {t}
            {tab === t && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
          </button>
        ))}
      </nav>

      {tab === "overview" ? (
        /* Overview — two even halves: About on the left, recent published skills on the right. */
        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* About — bio, email (if public), and a "Connect" row of social chips, in one card. */}
          <aside>
            <div className="space-y-7">
              {/* Bio */}
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Eyebrow>Bio</Eyebrow>
                  {owner && (
                    <button onClick={() => setBioOpen(true)}
                      className="rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary transition hover:bg-primary/20">
                      {profile.bio ? "Edit" : "Add"}
                    </button>
                  )}
                </div>
                {profile.bio ? (
                  <p className="mt-2.5 whitespace-pre-wrap text-sm leading-relaxed text-text/80">{profile.bio}</p>
                ) : (
                  <p className="mt-2.5 text-sm text-text/40">{owner ? "Add a short bio to tell people what you build." : "No bio yet."}</p>
                )}
              </div>

              {/* Email */}
              {(owner || profile.email) && (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Eyebrow>Email</Eyebrow>
                    {owner && (
                      <button onClick={toggleEmail}
                        className="flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-elev)] px-2.5 py-1 text-[11px] font-medium text-text/70 transition hover:border-primary/40 hover:text-primary">
                        <span className={`h-1.5 w-1.5 rounded-full ${profile.show_email ? "bg-emerald-500" : "bg-text/30"}`} />
                        {profile.show_email ? "Hide" : "Show"}
                      </button>
                    )}
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] text-text/55"><MailIcon /></span>
                    {profile.email ? (
                      <a href={`mailto:${profile.email}`} className="truncate text-sm text-text/80 transition hover:text-primary">{profile.email}</a>
                    ) : (
                      <span className="text-sm text-text/40">—</span>
                    )}
                  </div>
                  {owner && (
                    <p className="mt-1.5 text-xs text-text/40">
                      {profile.show_email ? "Shown on your public profile" : "Only visible to you"}
                    </p>
                  )}
                </div>
              )}

              {/* Connect — social links as icon chips; owner sees dashed chips to add the missing ones. */}
              {(owner || SOCIALS.some((s) => profile.social_links[s.key])) && (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Eyebrow>Connect</Eyebrow>
                    {owner && (
                      <button onClick={() => setConnectOpen(true)}
                        className="rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary transition hover:bg-primary/20">
                        {SOCIALS.some((s) => profile.social_links[s.key]) ? "Edit" : "Add"}
                      </button>
                    )}
                  </div>
                  <div className="mt-3 space-y-2">
                    {SOCIALS.map((s) => {
                      const v = profile.social_links[s.key];
                      // Viewers don't see links that aren't set; the owner sees every row (unset
                      // ones read "Not added") — edits happen via the header "Edit profile" button.
                      if (!v && !owner) return null;
                      return (
                        <div key={s.key} className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] text-text/60">
                            <SocialIcon kind={s.key} />
                          </span>
                          {v ? (
                            <a href={v} target="_blank" rel="noreferrer" title={v}
                              className="min-w-0 flex-1 truncate text-sm text-primary transition hover:underline">
                              {prettyUrl(v)}
                            </a>
                          ) : (
                            <span className="min-w-0 flex-1 truncate text-sm text-text/40">Not added</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Published skills — recent 3, with a jump to the Skills tab. */}
          <section>
            <Eyebrow>Published skills</Eyebrow>
            <div className="mt-4">
              {skills === null ? (
                <p className="text-sm text-text/40">Loading…</p>
              ) : recent.length === 0 ? (
                <p className="text-sm text-text/40">{owner ? "You haven't published a public skill yet." : "No published skills yet."}</p>
              ) : (
                <div className="space-y-3">
                  {recent.map((s) => <SkillRow key={s.id} skill={s} />)}
                </div>
              )}
            </div>
            {profile.public_skills > 3 && (
              <button onClick={() => setTab("skills")} className="mt-4 text-sm font-medium text-primary hover:underline">
                See all {profile.public_skills} skills →
              </button>
            )}
          </section>
        </div>
      ) : (
        /* Skills — the full published-skills list only, no About. Fills the full width. 20 per page. */
        <div className="mt-8">
          <Eyebrow>Published skills</Eyebrow>
          <div className="mt-4">
            {skills === null ? (
              <p className="text-sm text-text/40">Loading…</p>
            ) : skillsList.length === 0 ? (
              <p className="text-sm text-text/40">{owner ? "You haven't published a public skill yet." : "No published skills yet."}</p>
            ) : (
              <>
                {pager.total > pager.pageSize && (
                  <div className="mb-4 flex items-center justify-end">
                    <Pagination page={pager.page} pageSize={pager.pageSize} total={pager.total} onPage={pager.setPage} showSummary showNav={false} showSteps stepLabels />
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {pager.paged.map((s) => <SkillRow key={s.id} skill={s} />)}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editOpen && (
        <EditProfileModal profile={profile} token={accessToken!} onClose={() => setEditOpen(false)} onSaved={onSaved} />
      )}
      {bioOpen && (
        <BioModal
          value={profile.bio ?? ""}
          onClose={() => setBioOpen(false)}
          onSave={async (v) => { await patch({ bio: v || null }); setBioOpen(false); }}
        />
      )}
      {connectOpen && (
        <ConnectModal profile={profile} token={accessToken!}
          onClose={() => setConnectOpen(false)}
          onSaved={(p) => { onSaved(p); setConnectOpen(false); }} />
      )}
      {listOpen && (
        <FollowListModal username={profile.username} initialKind={listOpen}
          followers={profile.followers} following={profile.following} onClose={() => setListOpen(null)}
          // Following/unfollowing someone in the list changes the viewer's own following count. That
          // only affects the numbers on this page when the viewer is looking at their own profile —
          // keep the header stat + modal tab count in sync instead of going stale until a reload.
          onFollowChange={owner ? (delta) => setProfile((p) => (p ? { ...p, following: Math.max(0, p.following + delta) } : p)) : undefined} />
      )}
    </main>
  );
}

function SkillRow({ skill }: { skill: Skill }) {
  return (
    <Link to={`/skill/${skill.slug}`}
      className="group flex items-start gap-4 rounded-xl border border-[var(--border)] bg-[var(--bg-elev)] px-4 py-3.5 transition hover:border-primary/40">
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-primary">
        <BoltIcon />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-semibold text-text transition group-hover:text-primary">{skill.name}</p>
        {skill.summary && <p className="truncate text-xs text-text/50">{skill.summary}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <span className="font-mono text-[11px] text-text/40">{timeAgo(skill.created_at)}</span>
        {typeof skill.security_score === "number" && (
          <span className="rounded-md border border-primary/30 px-2 py-1 font-mono text-[11px] font-medium text-primary">
            SCORE {skill.security_score}
          </span>
        )}
      </div>
    </Link>
  );
}


const BIO_MAX = 300;

// Bio editor — a focused modal with a live character counter (e.g. 130/300).
function BioModal({ value, onClose, onSave }: { value: string; onClose: () => void; onSave: (v: string) => Promise<void> }) {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Grow the textarea to fit its content so it never shows an internal scrollbar.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 132), 320)}px`;
  }, [draft]);

  async function save() {
    if (saving) return;
    setSaving(true); setErr(null);
    try { await onSave(draft.trim()); }
    catch (e) { setErr(e instanceof Error ? e.message : "Couldn't save your bio."); setSaving(false); }
  }

  const near = draft.length >= BIO_MAX - 20;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-[22px] border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-5">
          <h3 className="font-display text-base font-semibold">Edit bio</h3>
          <button onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-[var(--glass-strong)] hover:text-text">✕</button>
        </div>
        <div className="px-6 py-6">
          {/* Textarea + counter share one bordered surface so the count reads as part of the field. */}
          <div className="rounded-xl border border-[var(--border)] bg-[var(--glass)] transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15">
            <textarea ref={ref} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={BIO_MAX}
              placeholder="Tell people what you build"
              className="block w-full resize-none overflow-hidden bg-transparent px-3.5 pt-3 text-sm leading-relaxed outline-none placeholder:text-text/30" />
            <div className="flex items-center justify-end px-3.5 pb-2.5">
              <span className={`font-mono text-[11px] tabular-nums ${near ? "text-primary" : "text-text/35"}`}>{draft.length}/{BIO_MAX}</span>
            </div>
          </div>
          {err && <p className="mt-2 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-500">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button onClick={onClose} className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm text-text/70 transition hover:bg-[var(--glass-strong)]">Cancel</button>
          <button onClick={save} disabled={saving}
            className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white shadow-btn transition hover:bg-primary-hover disabled:opacity-50 disabled:shadow-none">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Single edit-profile modal — populated with the current profile, edits everything in one place.
function EditProfileModal({
  profile, token, onClose, onSaved,
}: {
  profile: Profile;
  token: string;
  onClose: () => void;
  onSaved: (p: Profile) => void;
}) {
  const [displayName, setDisplayName] = useState(profile.display_name);
  const [username, setUsername] = useState(profile.username);
  const [bio, setBio] = useState(profile.bio ?? "");
  const [links, setLinks] = useState<SocialLinks>({
    linkedin: profile.social_links.linkedin ?? "",
    github: profile.social_links.github ?? "",
    x: profile.social_links.x ?? "",
  });
  const [nameStatus, setNameStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [checking, setChecking] = useState(false);
  // Flashed when a disallowed handle character is typed/pasted (stripped on the way in).
  const [charError, setCharError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bioRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow the bio field so it never shows an internal scrollbar.
  useEffect(() => {
    const el = bioRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 88), 220)}px`;
  }, [bio]);

  const usernameChanged = username.trim() !== profile.username;

  useEffect(() => {
    if (!usernameChanged) { setNameStatus(null); setChecking(false); return; }
    let cancelled = false;
    setChecking(true); setNameStatus(null);
    const t = setTimeout(async () => {
      try {
        const r = await checkUsername(username, token);
        if (!cancelled) setNameStatus({ ok: r.available, msg: r.available ? "Available" : (r.reason ?? "Unavailable") });
      } catch { if (!cancelled) setNameStatus(null); }
      finally { if (!cancelled) setChecking(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username, usernameChanged, token]);

  async function save() {
    if (saving) return;
    setSaving(true); setErr(null);
    try {
      const body: Parameters<typeof updateProfile>[0] = {
        display_name: displayName.trim(),
        bio: bio.trim() || null,
        social_links: {
          linkedin: links.linkedin?.trim() || null,
          github: links.github?.trim() || null,
          x: links.x?.trim() || null,
        },
      };
      if (usernameChanged) body.username = username.trim();
      onSaved(await updateProfile(body, token));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  }

  const saveDisabled = saving || !displayName.trim() || (usernameChanged && !(nameStatus?.ok ?? false));

  const inputCls = "w-full rounded-xl border border-[var(--border)] bg-[var(--glass)] px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 placeholder:text-text/30";
  const rowCls = "flex items-center gap-2.5 rounded-xl border border-[var(--border)] bg-[var(--glass)] px-3.5 py-2.5 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-[22px] border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header — avatar preview + title, sitting over a soft brand glow. */}
        <div className="relative flex items-center gap-3 border-b border-[var(--border)] px-6 py-5">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-16" style={{ background: "var(--glow-warm)" }} />
          <div className="relative"><Avatar url={profile.avatar_url} name={displayName || profile.display_name} size={44} /></div>
          <div className="relative min-w-0 flex-1">
            <h3 className="font-display text-base font-semibold text-text">Edit profile</h3>
            <p className="truncate text-xs text-text/50">This is how others see you on SkillPatch.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="relative flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-[var(--glass-strong)] hover:text-text">✕</button>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {/* Identity */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Display name">
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" className={inputCls} />
            </Field>

            <Field label="Username">
              <div className={rowCls}>
                <span className="font-mono text-text/40">@</span>
                <input value={username} onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^A-Za-z0-9_.]/g, "");
                  setCharError(cleaned !== e.target.value);
                  setUsername(cleaned);
                }} maxLength={30}
                  className="w-full bg-transparent font-mono text-sm outline-none" />
                {!charError && usernameChanged && (
                  checking ? (
                    <span className="shrink-0 font-mono text-[11px] text-text/40">checking…</span>
                  ) : nameStatus ? (
                    <span className={`shrink-0 text-sm ${nameStatus.ok ? "text-emerald-500" : "text-red-500"}`}>{nameStatus.ok ? "✓" : "✕"}</span>
                  ) : null
                )}
              </div>
              {charError ? (
                <p className="mt-1.5 text-xs text-red-500">Only letters, numbers, dot and underscore are allowed.</p>
              ) : usernameChanged && !checking && nameStatus && (
                <p className={`mt-1.5 text-xs ${nameStatus.ok ? "text-emerald-500" : "text-red-500"}`}>{nameStatus.msg}</p>
              )}
              <p className="mt-1.5 text-[11px] text-text/35">Changing this updates your profile link (skillpatch.com/u/{username || "…"}).</p>
            </Field>
          </div>

          <div className="h-px bg-[var(--border)]" />

          {/* Bio */}
          <Field label="Bio">
            <textarea ref={bioRef} value={bio} onChange={(e) => setBio(e.target.value)} maxLength={BIO_MAX} placeholder="Tell people what you build"
              className={`resize-none overflow-hidden leading-relaxed ${inputCls}`} />
            <p className={`mt-1 text-right font-mono text-[11px] tabular-nums ${bio.length >= BIO_MAX - 20 ? "text-primary" : "text-text/35"}`}>{bio.length}/{BIO_MAX}</p>
          </Field>

          <div className="h-px bg-[var(--border)]" />

          {/* Links */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {SOCIALS.map((s) => (
              <Field key={s.key} label={s.label} icon={<SocialIcon kind={s.key} />}>
                <input value={links[s.key] ?? ""} onChange={(e) => setLinks((l) => ({ ...l, [s.key]: e.target.value }))} placeholder={s.placeholder} className={inputCls} />
              </Field>
            ))}
          </div>

          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-500">{err}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button onClick={onClose} className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm text-text/70 transition hover:bg-[var(--glass-strong)]">Cancel</button>
          <button onClick={save} disabled={saveDisabled}
            className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white shadow-btn transition hover:bg-primary-hover disabled:opacity-50 disabled:shadow-none">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Focused modal for just the Connect links — same shell as Edit profile, only the social fields.
function ConnectModal({
  profile, token, onClose, onSaved,
}: {
  profile: Profile;
  token: string;
  onClose: () => void;
  onSaved: (p: Profile) => void;
}) {
  const [links, setLinks] = useState<SocialLinks>({
    linkedin: profile.social_links.linkedin ?? "",
    github: profile.social_links.github ?? "",
    x: profile.social_links.x ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (saving) return;
    setSaving(true); setErr(null);
    try {
      onSaved(await updateProfile({
        social_links: {
          linkedin: links.linkedin?.trim() || null,
          github: links.github?.trim() || null,
          x: links.x?.trim() || null,
        },
      }, token));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save your links.");
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-xl border border-[var(--border)] bg-[var(--glass)] px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15 placeholder:text-text/30";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-[22px] border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative flex items-center gap-3 border-b border-[var(--border)] px-6 py-5">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-16" style={{ background: "var(--glow-warm)" }} />
          <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--glass)] text-text/60">
            <LinkIcon />
          </span>
          <div className="relative min-w-0 flex-1">
            <h3 className="font-display text-base font-semibold text-text">{SOCIALS.some((s) => profile.social_links[s.key]) ? "Edit links" : "Add links"}</h3>
            <p className="truncate text-xs text-text/50">Where people can find you.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="relative flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-[var(--glass-strong)] hover:text-text">✕</button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6">
          {SOCIALS.map((s) => (
            <Field key={s.key} label={s.label} icon={<SocialIcon kind={s.key} />}>
              <input value={links[s.key] ?? ""} onChange={(e) => setLinks((l) => ({ ...l, [s.key]: e.target.value }))} placeholder={s.placeholder} className={inputCls} />
            </Field>
          ))}
          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-500">{err}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button onClick={onClose} className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm text-text/70 transition hover:bg-[var(--glass-strong)]">Cancel</button>
          <button onClick={save} disabled={saving}
            className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white shadow-btn transition hover:bg-primary-hover disabled:opacity-50 disabled:shadow-none">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-text">
        {icon && <span className="text-text/50">{icon}</span>}{label}
      </label>
      {children}
    </div>
  );
}

// -- Followers / Following list ---------------------------------------------
// One modal with Followers/Following tabs; opens to whichever the user clicked. Fixed height so it
// doesn't resize as the list (or a search) shrinks.
function FollowListModal({ username, initialKind, followers, following, onClose, onFollowChange }: {
  username: string;
  initialKind: "followers" | "following";
  followers: number;
  following: number;
  onClose: () => void;
  onFollowChange?: (delta: number) => void;
}) {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<"followers" | "following">(initialKind);
  const [items, setItems] = useState<FollowUser[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async (cur: string | null) => {
    setLoading(true); setErr(null);
    try {
      const fn = tab === "followers" ? getFollowers : getFollowing;
      const res = await fn(username, cur, accessToken, q || undefined);
      setItems((prev) => (cur ? [...prev, ...res.items] : res.items));
      setCursor(res.next_cursor);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load the list.");
    } finally {
      setLoading(false);
    }
  }, [username, tab, accessToken, q]);

  // Refetch from the top whenever the tab or (debounced) query changes.
  useEffect(() => { fetchPage(null); }, [fetchPage]);

  function switchTab(next: "followers" | "following") {
    if (next === tab) return;
    setItems([]); setCursor(null); setSearch(""); setTab(next);
  }

  const TabBtn = ({ id, label, count }: { id: "followers" | "following"; label: string; count: number }) => (
    <button onClick={() => switchTab(id)}
      className={`relative flex items-center gap-1.5 pb-3 text-sm font-medium transition ${tab === id ? "text-text" : "text-text/50 hover:text-text/80"}`}>
      {label} <span className="font-mono text-xs text-text/40">{count}</span>
      {tab === id && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex h-[600px] max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-[20px] border border-[var(--border)] bg-[var(--bg-elev)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Tabs + close over a soft brand glow. */}
        <div className="relative px-6 pt-5">
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-12" style={{ background: "var(--glow-warm)" }} />
          <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-[var(--glass-strong)] hover:text-text">✕</button>
          <nav className="relative flex gap-6 border-b border-[var(--border)]">
            <TabBtn id="followers" label="Followers" count={followers} />
            <TabBtn id="following" label="Following" count={following} />
          </nav>
        </div>

        {/* Search */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--glass)] px-3 py-2 transition focus-within:border-primary">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text/35">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${tab}…`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-text/35" />
            {search && (
              <button onClick={() => setSearch("")} aria-label="Clear" className="shrink-0 text-text/30 transition hover:text-text/70">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            )}
          </div>
        </div>

        {/* Fixed-height scroll area — modal size stays stable regardless of result count. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {err && <p className="px-3 py-4 text-sm text-red-500">{err}</p>}
          {loading && items.length === 0 && (
            <div className="flex items-center justify-center gap-2 px-3 py-10 text-xs text-text/45">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-text/20 border-t-primary" />
              Loading…
            </div>
          )}
          {!err && !loading && items.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-text/40">
              {q ? `No ${tab} match “${q}”.` : `No ${tab} yet.`}
            </p>
          )}
          {items.map((u) => <FollowRow key={u.id} u={u} onNavigate={onClose} onFollowChange={onFollowChange} />)}
          {cursor && (
            <button onClick={() => fetchPage(cursor)} disabled={loading}
              className="mt-1 w-full rounded-xl py-2.5 text-xs font-medium text-primary transition hover:bg-[var(--glass-strong)] disabled:opacity-50">
              {loading ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// A single row in the followers/following list — stretched link to the profile + an inline Follow toggle.
function FollowRow({ u, onNavigate, onFollowChange }: { u: FollowUser; onNavigate: () => void; onFollowChange?: (delta: number) => void }) {
  const { user, accessToken } = useAuth();
  const [following, setFollowing] = useState(u.is_following);
  const [busy, setBusy] = useState(false);
  const isSelf = user?.username === u.username;

  async function toggle(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    if (!accessToken || busy) return;
    setBusy(true);
    try {
      const was = following;
      const r = following ? await unfollowUser(u.username, accessToken) : await followUser(u.username, accessToken);
      setFollowing(r.following);
      // Bubble the change so the parent can keep the header/modal counts in sync (only a real
      // transition counts — the follow endpoints are idempotent).
      if (r.following !== was) onFollowChange?.(r.following ? 1 : -1);
    } catch { /* keep prior state */ } finally { setBusy(false); }
  }

  return (
    <div className="relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-[var(--glass-strong)]">
      <Link to={`/u/${u.username}`} onClick={onNavigate} aria-label={u.display_name} className="absolute inset-0" />
      <span className="pointer-events-none relative"><Avatar url={u.avatar_url} name={u.display_name} size={44} /></span>
      <span className="pointer-events-none relative min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-text">{u.display_name}</span>
        <span className="block truncate font-mono text-xs text-text/45">@{u.username}</span>
      </span>
      {user && !isSelf && (
        <button onClick={toggle} disabled={busy}
          className={`pointer-events-auto relative z-10 shrink-0 rounded-lg px-4 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
            following
              ? "border border-[var(--border)] text-text/80 hover:border-red-400 hover:text-red-500"
              : "bg-primary text-white hover:bg-primary-hover"
          }`}>
          {following ? "Following" : "Follow"}
        </button>
      )}
    </div>
  );
}
